import {AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Observable, Subscription} from 'rxjs';
import {delay, skip} from 'rxjs/operators';
import {AdRequestOptions, AdRequestParams, AdRReportType, ReportTemplate} from '../../interfaces/ad-reports';
import {PageComponent} from '../page/page.component';
import {ReportParamsComponent} from './report-params/report-params.component';
import {Serializer} from '../../interfaces/serializer';
import {SavedReport} from '../../interfaces/saved-reports';
import {ReportUtils} from '../../libraries/report-utils';
import {FormGroup} from '@angular/forms';
import {FullscreenService} from '../../services/fullscreen.service';
import {ReportStore} from "../../store/report/report.store";
import {reportOpenShareWithLinkDialog} from "../../store/report/report.actions";
import {AppState} from "../../store/store";
import {Store} from "@ngrx/store";
import {ExitReportGuardService} from '../../services/exit-report-guard.service';
import {TranslateService} from '@ngx-translate/core';
import {ParamConfig} from '../../interfaces/params';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss']
})
export class ReportComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(PageComponent)           public readonly   pageC:                    PageComponent;
  @ViewChild(ReportParamsComponent)   public readonly   paramsC:                  ReportParamsComponent;

  @Input('form')                      public            form:                     FormGroup     =       new FormGroup<any>({});
  @Input('paramsConfig')              public            _paramsConfig:            Array<string> =       ['dimensions', 'metrics', 'new_period'];
  @Input('actionsConfig')             public            _actionsConfig:           Array<string> =       ['filters', 'comparison'];
  @Input('paramsDropdownConfig')      public            _paramsDropdownConfig:    Array<string> =       [];
  @Input('helpTemplate')              public            _helpTemplate:            string;
  @Input('beta')                      public            beta:                     boolean =             false;

  @Input('anonymous')                 public            anonymous:                boolean =             false;
  @Input('iframe')                    public            iframe:                   boolean =             false;
  @Input('disableExports')            public            disableExports:           boolean =             false;
  @Input()                                               public            disableDownloads:         boolean =             false;
  @Input('disablePublic')             public            disablePublic:            boolean =             false;
  @Input()                                               public            disableComparison:        boolean =             false;

  @Input()                                               public            enableDialogs:            boolean =             false;

  @Output() public readonly onReady: EventEmitter<void> = new EventEmitter<void>();
  @Output() public readonly onFullscreen: EventEmitter<void> = new EventEmitter<void>();

  public readonly parameters: any = {
    performance: {
      paramsConfig:           ['dimensions', 'metrics', 'new_period'],
      actionsConfig:          ['filters', 'comparison'],
      paramsDropdownConfig:   [],
      helpTemplate:           'performance-report'
    },
    cycle: {
      paramsConfig:           ['dimensions', 'metric', 'new_period_without_comparison', 'cycleMetricsTracking'],
      actionsConfig:          ['filters'],
      paramsDropdownConfig:   [],
      helpTemplate:           'cycle-report'
    }
  };


  private fullscreenSubs: Subscription;

  public readonly name$: Observable<string> = this._reportStore.name$;
  public readonly report$: Observable<Serializer<SavedReport> | null> = this._reportStore.report$;
  public readonly requestParams$: Observable<Partial<AdRequestParams>> = this._reportStore.requestParams$;
  public readonly requestOptions$: Observable<Partial<AdRequestOptions>> = this._reportStore.requestOptions$;
  public readonly loaded$: Observable<boolean> = this._reportStore.initialized$;
  public readonly reportType$: Observable<AdRReportType> = this._reportStore.reportType$;
  public readonly loading$: Observable<boolean> = this._reportStore.loading$;
  public readonly paramsConfigs$: Observable<Array<ParamConfig>> = this._reportStore.paramsConfigs$;
  public readonly actionsConfigs$: Observable<Array<ParamConfig>> = this._reportStore.actionsConfigs$;
  public readonly paramsDropdownConfigs$: Observable<Array<ParamConfig>> = this._reportStore.paramsDropdownConfigs$;
  public readonly helpTemplate$: Observable<string> = this._reportStore.helpTemplate$;
  public readonly isPublic$: Observable<boolean> = this._reportStore.isPublic$;
  public readonly template$: Observable<Serializer<ReportTemplate> | null> = this._reportStore.template$;
  public readonly type$: Observable<'report' | 'template' | 'url' | 'local' | 'public' | null> = this._reportStore.type$;
  public readonly publicMode$: Observable<boolean> = this._reportStore.publicMode$;
  public readonly publicLink$: Observable<string> = this._reportStore.publicLink$;
  public readonly submitted$: Observable<boolean> = this._reportStore.submitted$;
  public readonly authorize$: Observable<boolean> = this._reportStore.authorize$;
  public readonly owner$: Observable<string> = this._reportStore.owner$;

  constructor(
    public readonly route: ActivatedRoute,
    public readonly fullscreenS: FullscreenService,
    private readonly _reportStore: ReportStore,
    private readonly _exitReportGuardService: ExitReportGuardService,
    private readonly _translateService: TranslateService,
    private readonly _store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this._reportStore.init(
      this.parameters,
      this.iframe,
      this._paramsConfig,
      this._actionsConfig,
      this._paramsDropdownConfig,
      this._helpTemplate,
      this.enableDialogs
    );
  }

  ngAfterViewInit(): void {
    this.fullscreenSubs = this.fullscreenS.onFullscreen$
      .pipe(
        skip(1),
        delay(0)
      )
      .subscribe(() => {
        this.onFullscreen.emit();
      });
  }

  ngOnDestroy(): void {
    ReportUtils.unsubscribe(this.fullscreenSubs);
  }

  public onOpenPublicSharingDialog(): void {
    this._store.dispatch(reportOpenShareWithLinkDialog());
  }

  @HostListener('window:beforeunload', ['$event']) showMessage($event: any) {
    if (!this._exitReportGuardService.canExitBehaviorSubject.value) {
      $event.returnValue = this._translateService.instant('reports.reload_message');
    }
  }

}
